import type { Response } from '#types/common.ts';

import { api } from '#services/http-clients.ts';

export const transferQueueParticipantTypes = [
  'patient',
  'scheduledProvider',
  'provider',
  'staff',
  'anonymous',
  'phone',
  'guest',
] as const;

export type TransferQueueParticipantType = (typeof transferQueueParticipantTypes)[number];
export type TransferQueueSortField =
  | 'appointmentId'
  | 'enteredQueueAt'
  | 'exitedQueueAt'
  | 'secondsInQueue';
export type TransferQueueSortDirection = 'ASC' | 'DESC';

interface TransferQueueFilters {
  /** Beginning of the queue-entry period in `yyyy-MM-dd HH:mm:ss` format. */
  minEnteredAt: string;
  /** End of the queue-entry period in `yyyy-MM-dd HH:mm:ss` format. */
  maxEnteredAt: string;
  timezone?: string;
  appointmentId?: number;
  providerId?: number[];
  appointmentStatusId?: number[];
  assignedUserId?: number[];
  appointmentTypeId?: number[];
  addressId?: number[];
  connectionUserId?: number[];
  connectionParticipantType?: TransferQueueParticipantType[];
  isCurrentlyQueued?: 0 | 1;
  patientDisconnected?: 0 | 1;
}

export type GetTransferQueueStatsParams = TransferQueueFilters;

interface TransferQueueTrend {
  rawValue: number;
  direction: 'up' | 'down' | 'neutral';
  comparisonPeriod: string;
  dataCompleteness: number;
}

interface TransferQueueStats {
  enteredCount: number;
  exitedCount: number;
  currentlyQueuedCount: number;
  averageWaitSeconds: number | null;
  longestCompletedWaitSeconds: number | null;
  averageCurrentAgeSeconds: number | null;
  oldestCurrentAgeSeconds: number | null;
  appointmentsWithConnectionsCount: number;
  connectionCount: number;
  patientConnectedCount: number;
  providerConnectedCount: number;
  patientDisconnectedCount: number;
  patientDisconnectedRate: number | null;
  averageWaitTrend: TransferQueueTrend | null;
  longestCompletedWaitTrend: TransferQueueTrend | null;
  patientDisconnectedTrend: TransferQueueTrend | null;
}

interface TransferQueueTrendWire {
  rawValue: unknown;
  direction: unknown;
  comparisonPeriod: unknown;
  dataCompleteness: unknown;
}

interface TransferQueueStatsWire {
  enteredCount: unknown;
  exitedCount: unknown;
  currentlyQueuedCount: unknown;
  averageWaitSeconds: unknown;
  longestCompletedWaitSeconds: unknown;
  averageCurrentAgeSeconds: unknown;
  oldestCurrentAgeSeconds: unknown;
  appointmentsWithConnectionsCount: unknown;
  connectionCount: unknown;
  patientConnectedCount: unknown;
  providerConnectedCount: unknown;
  patientDisconnectedCount: unknown;
  patientDisconnectedRate: unknown;
  averageWaitTrend: TransferQueueTrendWire | null;
  longestCompletedWaitTrend: TransferQueueTrendWire | null;
  patientDisconnectedTrend: TransferQueueTrendWire | null;
}

export interface TransferQueueStatusTransition {
  occurredAt: string;
  priorStatusKnown: 0 | 1;
  priorStatusId: number | null;
  priorStatusName: string | null;
  newStatusId: number | null;
  newStatusName: string | null;
  priorAssignedUserId: number | null;
  newAssignedUserId: number | null;
  changedByUserId: number | null;
  changedByUserName: string | null;
}

export interface TransferQueueConnection {
  id: number;
  participantType: TransferQueueParticipantType;
  userId: number | null;
  chartId: string | null;
  role: string | null;
  displayName: string | null;
  connectedAt: string;
  disconnectedAt: string | null;
  connectedWhileQueued: 0 | 1;
  connectedAtQueueEntry: 0 | 1;
  disconnectedWhileQueued: 0 | 1;
  secondsConnectedWhileQueued: number;
}

export interface TransferQueueAppointment {
  orgId: number;
  appointmentId: number;
  episodeNumber: number;
  enteredQueueAt: string;
  exitedQueueAt: string | null;
  secondsInQueue: number;
  isCurrentlyQueued: 0 | 1;
  entryStatusId: number;
  entryStatusName: string;
  finalStatusId: number | null;
  finalStatusName: string | null;
  entryAssignedUserId: number | null;
  entryAssignedUserName: string | null;
  enteredByUserId: number | null;
  enteredByUserName: string | null;
  connectionCount: number;
  participantCount: number;
  patientConnected: 0 | 1;
  providerConnected: 0 | 1;
  patientDisconnected: 0 | 1;
  patientDisconnectCount: number;
  firstPatientConnectionAt: string | null;
  firstPatientDisconnectAt: string | null;
  lastPatientDisconnectAt: string | null;
  firstProviderConnectionAt: string | null;
  providerId: number | null;
  providerFirstName: string | null;
  providerLastName: string | null;
  patientId: number | null;
  patientChartId: string | null;
  patientFirstName: string | null;
  patientLastName: string | null;
  appointmentTypeId?: number | null;
  appointmentTypeName?: string | null;
  addressId?: number | null;
  addressName?: string | null;
  statusTransitions: TransferQueueStatusTransition[];
  connections: TransferQueueConnection[];
}

export interface GetTransferQueueAppointmentsParams extends TransferQueueFilters {
  page?: number;
  pageSize?: number;
  sortBy?: TransferQueueSortField;
  sortDirection?: TransferQueueSortDirection;
}

type TransferQueueAppointmentsPage = {
  data: TransferQueueAppointment[];
  page: number;
  totalItems: number;
  totalPages: number;
};

export function getTransferQueueStats(
  params: GetTransferQueueStatsParams
): Promise<TransferQueueStats> {
  return api
    .get<Response<{ data: TransferQueueStatsWire }>>('/insights/transfer-queue-stats', { params })
    .then((response) => normalizeTransferQueueStats(response.data.payload.data));
}

export function getTransferQueueAppointments(
  params: GetTransferQueueAppointmentsParams
): Promise<TransferQueueAppointmentsPage> {
  return api
    .get<Response<TransferQueueAppointmentsPage>>('/insights/transfer-queue-appointments', {
      params,
    })
    .then((response) => response.data.payload);
}

function normalizeTransferQueueStats(stats: TransferQueueStatsWire): TransferQueueStats {
  return {
    enteredCount: toNumber(stats.enteredCount) ?? 0,
    exitedCount: toNumber(stats.exitedCount) ?? 0,
    currentlyQueuedCount: toNumber(stats.currentlyQueuedCount) ?? 0,
    averageWaitSeconds: toNumber(stats.averageWaitSeconds),
    longestCompletedWaitSeconds: toNumber(stats.longestCompletedWaitSeconds),
    averageCurrentAgeSeconds: toNumber(stats.averageCurrentAgeSeconds),
    oldestCurrentAgeSeconds: toNumber(stats.oldestCurrentAgeSeconds),
    appointmentsWithConnectionsCount: toNumber(stats.appointmentsWithConnectionsCount) ?? 0,
    connectionCount: toNumber(stats.connectionCount) ?? 0,
    patientConnectedCount: toNumber(stats.patientConnectedCount) ?? 0,
    providerConnectedCount: toNumber(stats.providerConnectedCount) ?? 0,
    patientDisconnectedCount: toNumber(stats.patientDisconnectedCount) ?? 0,
    patientDisconnectedRate: toNumber(stats.patientDisconnectedRate),
    averageWaitTrend: normalizeTransferQueueTrend(stats.averageWaitTrend),
    longestCompletedWaitTrend: normalizeTransferQueueTrend(stats.longestCompletedWaitTrend),
    patientDisconnectedTrend: normalizeTransferQueueTrend(stats.patientDisconnectedTrend),
  };
}

function normalizeTransferQueueTrend(
  trend: TransferQueueTrendWire | null
): TransferQueueTrend | null {
  if (!trend) return null;

  const rawValue = toNumber(trend.rawValue);
  const dataCompleteness = toNumber(trend.dataCompleteness);

  if (
    rawValue === null ||
    dataCompleteness === null ||
    !isTransferQueueTrendDirection(trend.direction)
  ) {
    return null;
  }

  return {
    rawValue,
    direction: trend.direction,
    comparisonPeriod: typeof trend.comparisonPeriod === 'string' ? trend.comparisonPeriod : '',
    dataCompleteness,
  };
}

function isTransferQueueTrendDirection(value: unknown): value is TransferQueueTrend['direction'] {
  return value === 'up' || value === 'down' || value === 'neutral';
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;

  const numberValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}
